import { useMemo } from 'react';
import { encode } from 'uqr';
import { cn } from '@/lib/utils';

/**
 * PFC-21 — the QR code a fiscal receipt carries, drawn as an SVG from the
 * stored payload (the tax service's check link on a Ukrainian receipt).
 *
 * Encoded in the browser with `uqr` (MIT, no dependencies). The modules are
 * one `<path>` built here rather than an SVG string injected into the page,
 * so nothing from the payload ever reaches the DOM as markup.
 */

/** The standard quiet zone: four modules of white around the code. */
const QUIET_ZONE = 4;

export type QrMatrix = { size: number; path: string };

/**
 * The dark modules of `value`'s QR code as one SVG path in module units, or
 * null when the payload is empty or too long for any QR version.
 */
export function qrMatrix(value: string): QrMatrix | null {
  if (value === '') return null;
  let data: boolean[][];
  try {
    data = encode(value, { ecc: 'M', border: QUIET_ZONE }).data;
  } catch {
    return null;
  }
  const parts: string[] = [];
  data.forEach((row, y) => {
    // One horizontal run per stretch of dark modules keeps the path short.
    let x = 0;
    while (x < row.length) {
      if (!row[x]) {
        x += 1;
        continue;
      }
      const start = x;
      while (x < row.length && row[x]) x += 1;
      parts.push(`M${start} ${y}h${x - start}v1h${start - x}z`);
    }
  });
  return { size: data.length, path: parts.join('') };
}

/**
 * The QR image with its content as small text under it. `label` is the
 * accessible name; the payload is appended so a screen reader announces the
 * link the image stands for.
 */
export function QrCode({
  value,
  label,
  className,
  align = 'center',
}: {
  value: string;
  label: string;
  className?: string;
  /** `start` in a label/value panel: image and link left-aligned, link kept near the image's width. */
  align?: 'center' | 'start';
}) {
  const matrix = useMemo(() => qrMatrix(value), [value]);
  return (
    <figure
      className={cn(
        'flex flex-col gap-1',
        align === 'start' ? 'items-start' : 'items-center',
        className,
      )}
      data-testid="qr-code"
    >
      {matrix !== null && (
        <svg
          role="img"
          aria-label={`${label}: ${value}`}
          viewBox={`0 0 ${matrix.size} ${matrix.size}`}
          shapeRendering="crispEdges"
          className="h-36 w-36 rounded-sm bg-white"
        >
          <path d={matrix.path} className="fill-black" />
        </svg>
      )}
      {/* Already in the image's accessible name when drawn: not read twice.
          `self-stretch` + `contain: inline-size`: the URL takes the width its
          container already has (receipt box or fiscal-block panel, centred or
          start-aligned) and wraps there, and never widens that container by
          itself (a DPS check URL is 100+ characters). */}
      <figcaption
        aria-hidden={matrix !== null ? true : undefined}
        className={cn(
          'self-stretch break-all font-mono text-[10px] leading-tight text-muted-foreground [contain:inline-size]',
          align === 'start' ? 'max-w-xs text-left' : 'text-center',
        )}
      >
        {value}
      </figcaption>
    </figure>
  );
}
